import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Bell,
  Trash2,
  Edit,
  Eye,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Composant Calendrier
const CalendarView = ({ appointments, selectedDate, onSelectDate, month, year, onMonthChange }) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const getAppointmentsForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments[dateStr] || [];
  };

  const days = [];
  // Jours vides avant le premier jour
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
  }
  // Jours du mois
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayAppts = getAppointmentsForDay(day);
    const isSelected = selectedDate === dateStr;
    const isToday = new Date().toISOString().split('T')[0] === dateStr;

    days.push(
      <div
        key={day}
        onClick={() => onSelectDate(dateStr)}
        className={`h-24 border border-gray-200 p-1 cursor-pointer transition-colors hover:bg-green-50
          ${isSelected ? 'bg-green-100 border-green-500' : ''}
          ${isToday ? 'border-2 border-green-600' : ''}`}
      >
        <div className={`text-sm font-medium ${isToday ? 'text-green-700' : 'text-gray-700'}`}>
          {day}
        </div>
        <div className="space-y-0.5 overflow-hidden">
          {dayAppts.slice(0, 3).map((apt, idx) => (
            <div
              key={idx}
              className={`text-xs px-1 py-0.5 rounded truncate
                ${apt.status === 'confirmed' ? 'bg-green-200 text-green-800' : ''}
                ${apt.status === 'pending' ? 'bg-yellow-200 text-yellow-800' : ''}
                ${apt.status === 'completed' ? 'bg-blue-200 text-blue-800' : ''}
                ${apt.status === 'cancelled' ? 'bg-red-200 text-red-800 line-through' : ''}
                ${apt.status === 'no_show' ? 'bg-gray-200 text-gray-600' : ''}`}
            >
              {apt.appointment_time} - {apt.customer_name?.split(' ')[0]}
            </div>
          ))}
          {dayAppts.length > 3 && (
            <div className="text-xs text-gray-500">+{dayAppts.length - 3} autres</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      {/* Navigation du mois */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-gray-800">
          {monthNames[month - 1]} {year}
        </h2>
        <button
          onClick={() => onMonthChange(1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* En-têtes des jours */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Grille des jours */}
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {days}
      </div>
    </div>
  );
};

// Modal de création/édition de rendez-vous
const AppointmentModal = ({ isOpen, onClose, appointment, onSave, contacts, deals }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    appointment_date: '',
    appointment_time: '',
    appointment_type: 'consultation',
    location: '4000, montée Masson, Laval (Québec) H7B 1B7',
    duration_minutes: 90,
    notes: '',
    contact_id: '',
    deal_id: '',
    status: 'confirmed'
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (appointment) {
      setFormData({
        customer_name: appointment.customer_name || '',
        customer_email: appointment.customer_email || '',
        customer_phone: appointment.customer_phone || '',
        appointment_date: appointment.appointment_date || '',
        appointment_time: appointment.appointment_time || '',
        appointment_type: appointment.appointment_type || 'consultation',
        location: appointment.location || '4000, montée Masson, Laval (Québec) H7B 1B7',
        duration_minutes: appointment.duration_minutes || 90,
        notes: appointment.notes || '',
        contact_id: appointment.contact_id || '',
        deal_id: appointment.deal_id || '',
        status: appointment.status || 'confirmed'
      });
    } else {
      // Reset pour nouveau RDV
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        appointment_date: '',
        appointment_time: '',
        appointment_type: 'consultation',
        location: '4000, montée Masson, Laval (Québec) H7B 1B7',
        duration_minutes: 90,
        notes: '',
        contact_id: '',
        deal_id: '',
        status: 'confirmed'
      });
    }
  }, [appointment, isOpen]);

  // Charger les créneaux disponibles quand la date change
  useEffect(() => {
    if (formData.appointment_date) {
      loadAvailableSlots(formData.appointment_date);
    }
  }, [formData.appointment_date]);

  const loadAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/appointments/available-slots?date=${date}`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Erreur chargement créneaux:', error);
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  // Remplir automatiquement depuis un contact
  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setFormData(prev => ({
        ...prev,
        contact_id: contactId,
        customer_name: `${contact.prenom || ''} ${contact.nom || ''}`.trim(),
        customer_email: contact.email || '',
        customer_phone: contact.telephone || ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  const appointmentTypes = [
    { value: 'consultation', label: '🏠 Consultation initiale' },
    { value: 'visite_terrain', label: '📍 Visite terrain' },
    { value: 'signature', label: '✍️ Signature contrat' },
    { value: 'suivi', label: '🔄 Suivi projet' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200" style={{ backgroundColor: '#335C2E' }}>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Calendar size={24} />
            {appointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Sélection contact CRM */}
          <div className="bg-green-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lier à un contact CRM (optionnel)
            </label>
            <select
              value={formData.contact_id}
              onChange={(e) => handleContactSelect(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">-- Sélectionner un contact --</option>
              {contacts.map(contact => (
                <option key={contact.id} value={contact.id}>
                  {contact.prenom} {contact.nom} ({contact.email})
                </option>
              ))}
            </select>
          </div>

          {/* Informations client */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User size={16} className="inline mr-1" /> Nom du client *
              </label>
              <input
                type="text"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Jean Tremblay"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail size={16} className="inline mr-1" /> Email *
              </label>
              <input
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="jean@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone size={16} className="inline mr-1" /> Téléphone *
              </label>
              <input
                type="tel"
                required
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="514-555-1234"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de rendez-vous
              </label>
              <select
                value={formData.appointment_type}
                onChange={(e) => setFormData({ ...formData, appointment_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                {appointmentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date et heure */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar size={16} className="inline mr-1" /> Date *
              </label>
              <input
                type="date"
                required
                value={formData.appointment_date}
                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Clock size={16} className="inline mr-1" /> Heure *
              </label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-2">
                  <RefreshCw className="animate-spin" size={20} />
                </div>
              ) : availableSlots.length > 0 ? (
                <select
                  required
                  value={formData.appointment_time}
                  onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">-- Sélectionner --</option>
                  {availableSlots.map(slot => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="time"
                  required
                  value={formData.appointment_time}
                  onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              )}
            </div>
          </div>

          {/* Lieu et durée */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin size={16} className="inline mr-1" /> Lieu
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Adresse ou 'Téléphone'"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée (minutes)
              </label>
              <select
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value={30}>30 min</option>
                <option value={60}>1 heure</option>
                <option value={90}>1h30</option>
                <option value={120}>2 heures</option>
              </select>
            </div>
          </div>

          {/* Statut (uniquement en édition) */}
          {appointment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="pending">⏳ En attente</option>
                <option value="confirmed">✅ Confirmé</option>
                <option value="completed">✓ Terminé</option>
                <option value="cancelled">❌ Annulé</option>
                <option value="no_show">⚠️ Absent</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Notes ou informations complémentaires..."
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-white rounded-lg font-medium"
              style={{ backgroundColor: '#335C2E' }}
            >
              {appointment ? 'Enregistrer' : 'Créer le rendez-vous'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Composant principal
const AppointmentsManagement = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [appointmentsByDay, setAppointmentsByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Supposer authentifié au départ
  const [view, setView] = useState('calendar'); // 'calendar' ou 'list'
  const [selectedDate, setSelectedDate] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);

  // Récupérer le token une seule fois
  const getHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Charger les rendez-vous
  const loadAppointments = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }
    
    setLoading(true);
    try {
      const headers = getHeaders();
      
      // Charger liste paginée
      const listParams = new URLSearchParams();
      listParams.append('limit', '200');
      if (search) listParams.append('search', search);
      if (statusFilter) listParams.append('status', statusFilter);

      const [listResponse, calendarResponse] = await Promise.all([
        axios.get(`${API}/appointments?${listParams}`, { headers }),
        axios.get(`${API}/appointments/calendar?month=${month}&year=${year}`, { headers })
      ]);

      setAppointments(listResponse.data.appointments || []);
      setAppointmentsByDay(calendarResponse.data.appointments_by_day || {});
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Erreur chargement RDV:', error);
      if (error.response?.status === 401) {
        // Token invalide
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      } else {
        toast.error('Erreur lors du chargement des rendez-vous');
      }
    }
    setLoading(false);
  }, [month, year, search, statusFilter]);

  // Charger les contacts pour le formulaire
  const loadContacts = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      console.warn('loadContacts: Pas de token');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      console.log('Chargement des contacts...');
      const response = await axios.get(`${API}/contacts?limit=500`, { headers });
      const contactsList = response.data.contacts || [];
      setContacts(contactsList);
      console.log(`✅ ${contactsList.length} contacts chargés pour le dropdown`);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
      if (error.response?.status === 401) {
        console.warn('Token invalide pour charger les contacts');
      }
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      console.warn('Pas de token disponible');
      setLoading(false);
      // Ne pas rediriger automatiquement - afficher un message à la place
      return;
    }
    
    // Charger les données
    loadAppointments();
    loadContacts();
  }, [month, year, loadAppointments, loadContacts]); // Recharger quand le mois/année change

  // Debounce pour les filtres
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    
    const debounce = setTimeout(() => {
      if (search || statusFilter) {
        loadAppointments();
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, statusFilter, loadAppointments]);

  // Changer de mois
  const handleMonthChange = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  // Ouvrir le modal de création/édition
  const openModal = async (appointment = null) => {
    // Recharger les contacts avant d'ouvrir le modal
    await loadContacts();
    setEditingAppointment(appointment);
    setIsModalOpen(true);
  };

  // Créer/modifier un RDV
  const handleSaveAppointment = async (formData) => {
    try {
      const headers = getHeaders();
      if (editingAppointment) {
        await axios.patch(`${API}/appointments/${editingAppointment.id}`, formData, { headers });
        toast.success('Rendez-vous modifié avec succès');
      } else {
        await axios.post(`${API}/appointments/admin`, formData, { headers });
        toast.success('Rendez-vous créé avec succès');
      }
      setIsModalOpen(false);
      setEditingAppointment(null);
      loadAppointments();
    } catch (error) {
      console.error('Erreur sauvegarde RDV:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // Supprimer un RDV
  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      const headers = getHeaders();
      await axios.delete(`${API}/appointments/${id}`, { headers });
      toast.success('Rendez-vous supprimé');
      loadAppointments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Envoyer un rappel
  const handleSendReminder = async (id) => {
    try {
      const headers = getHeaders();
      await axios.post(`${API}/appointments/${id}/send-reminder`, {}, { headers });
      toast.success('Rappels envoyés (client + équipe)');
      loadAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du rappel');
    }
  };

  // Changer le statut rapidement
  const handleQuickStatusChange = async (id, newStatus) => {
    try {
      const headers = getHeaders();
      await axios.patch(`${API}/appointments/${id}`, { status: newStatus }, { headers });
      toast.success('Statut mis à jour');
      loadAppointments();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Badge de statut
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: '⏳ En attente',
      confirmed: '✅ Confirmé',
      completed: '✓ Terminé',
      cancelled: '❌ Annulé',
      no_show: '⚠️ Absent'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Badge de type
  const TypeBadge = ({ type }) => {
    const labels = {
      consultation: '🏠 Consultation',
      visite_terrain: '📍 Visite terrain',
      signature: '✍️ Signature',
      suivi: '🔄 Suivi'
    };
    return (
      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
        {labels[type] || type}
      </span>
    );
  };

  // Rendez-vous du jour sélectionné
  const selectedDayAppointments = selectedDate ? (appointmentsByDay[selectedDate] || []) : [];

  // Afficher écran de connexion si pas authentifié
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <Calendar size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Connexion requise</h2>
          <p className="text-gray-600 mb-6">
            Vous devez être connecté pour accéder à la gestion des rendez-vous.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="text-white py-6 px-6" style={{ backgroundColor: '#335C2E' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-white/20 rounded-lg transition"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Calendar size={28} /> Gestion des Rendez-vous
                </h1>
                <p className="text-green-100 mt-1">Calendrier et disponibilités</p>
              </div>
            </div>
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-4 py-2 bg-white text-green-800 rounded-lg font-medium hover:bg-green-50 transition"
            >
              <Plus size={20} /> Nouveau RDV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Contrôles */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Toggle vue */}
            <div className="flex gap-2">
              <button
                onClick={() => setView('calendar')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  view === 'calendar' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Calendar size={18} className="inline mr-2" /> Calendrier
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  view === 'list' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter size={18} className="inline mr-2" /> Liste
              </button>
            </div>

            {/* Filtres */}
            <div className="flex gap-3">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 w-64"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmé</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
                <option value="no_show">Absent</option>
              </select>
              <button
                onClick={loadAppointments}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                title="Rafraîchir"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Vue Calendrier ou Liste */}
          <div className={view === 'calendar' ? 'lg:col-span-2' : 'lg:col-span-3'}>
            {view === 'calendar' ? (
              <CalendarView
                appointments={appointmentsByDay}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                month={month}
                year={year}
                onMonthChange={handleMonthChange}
              />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Heure</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {appointments.map(apt => (
                      <tr key={apt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{apt.appointment_date}</div>
                          <div className="text-sm text-gray-500">{apt.appointment_time}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{apt.customer_name}</div>
                          <div className="text-sm text-gray-500">{apt.customer_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <TypeBadge type={apt.appointment_type} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={apt.status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            {apt.status === 'pending' && (
                              <button
                                onClick={() => handleQuickStatusChange(apt.id, 'confirmed')}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="Confirmer"
                              >
                                <Check size={18} />
                              </button>
                            )}
                            {apt.status === 'confirmed' && !apt.reminder_sent && (
                              <button
                                onClick={() => handleSendReminder(apt.id)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                title="Envoyer rappel"
                              >
                                <Bell size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => openModal(apt)}
                              className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                              title="Modifier"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteAppointment(apt.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {appointments.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          Aucun rendez-vous trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Panneau latéral (calendrier uniquement) */}
          {view === 'calendar' && (
            <div className="space-y-4">
              {/* Légende */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Légende</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-200"></div>
                    <span>En attente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-200"></div>
                    <span>Confirmé</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-200"></div>
                    <span>Terminé</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-200"></div>
                    <span>Annulé</span>
                  </div>
                </div>
              </div>

              {/* Détails du jour sélectionné */}
              {selectedDate && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">
                    📅 {formatDate(selectedDate)}
                  </h3>
                  {selectedDayAppointments.length > 0 ? (
                    <div className="space-y-3">
                      {selectedDayAppointments.map(apt => (
                        <div key={apt.id} className="border-l-4 border-green-500 pl-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{apt.appointment_time}</span>
                            <StatusBadge status={apt.status} />
                          </div>
                          <div className="text-sm text-gray-800 mt-1">{apt.customer_name}</div>
                          <div className="text-xs text-gray-500">{apt.customer_email}</div>
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => openModal(apt)}
                              className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                            >
                              Modifier
                            </button>
                            {apt.status === 'pending' && (
                              <button
                                onClick={() => handleQuickStatusChange(apt.id, 'confirmed')}
                                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                              >
                                Confirmer
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Aucun rendez-vous ce jour</p>
                  )}
                  <button
                    onClick={() => openModal()}
                    className="w-full mt-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium"
                  >
                    <Plus size={16} className="inline mr-1" /> Ajouter un RDV ce jour
                  </button>
                </div>
              )}

              {/* Stats rapides */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Ce mois</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-800">
                      {Object.values(appointmentsByDay).flat().length}
                    </div>
                    <div className="text-xs text-gray-500">Total RDV</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">
                      {Object.values(appointmentsByDay).flat().filter(a => a.status === 'confirmed').length}
                    </div>
                    <div className="text-xs text-gray-500">Confirmés</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-700">
                      {Object.values(appointmentsByDay).flat().filter(a => a.status === 'pending').length}
                    </div>
                    <div className="text-xs text-gray-500">En attente</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700">
                      {Object.values(appointmentsByDay).flat().filter(a => a.status === 'completed').length}
                    </div>
                    <div className="text-xs text-gray-500">Terminés</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingAppointment(null); }}
        appointment={editingAppointment}
        onSave={handleSaveAppointment}
        contacts={contacts}
        deals={deals}
      />
    </div>
  );
};

export default AppointmentsManagement;
